import type { Survey, SurveySession } from '../../fetchSurvey/types';
import type { SurveyAnswers, SubmitInput } from '../types';

/**
 * Type guard function validating if input structure is a SubmitInput wrapper.
 *
 * @param input The answers map or input object.
 * @returns Boolean representing validation result.
 */
export function isSubmitInput(input: SurveyAnswers | SubmitInput): input is SubmitInput {
  return typeof input === 'object' && input !== null && 'instanceId' in input && 'answers' in input;
}

/**
 * Resolves raw input and session context to a clean, validated SubmitInput model.
 * Throws errors if session is uninitialized or missing active identifiers.
 *
 * @param input Raw selections or input object.
 * @param session Active survey session details.
 * @param survey Loaded survey definition.
 * @returns Resolved input model containing clean parameters.
 */
export function resolveSubmitInput(
  input: SurveyAnswers | SubmitInput,
  session?: SurveySession | null,
  survey?: Survey | null
): SubmitInput {
  if (isSubmitInput(input)) {
    return {
      ...input,
      surveyLanguage: input.surveyLanguage ?? session?.language ?? 'en',
      questions: input.questions ?? survey?.pages.flatMap(p => p.questions) ?? [],
    };
  }

  // Guard: require session context if not explicitly provided inside input payload
  if (!session?.instanceId) {
    throw new Error('Survey session not ready. Fetch survey before submitting.');
  }

  return {
    answers: input,
    instanceId: session.instanceId,
    surveyLanguage: session.language ?? 'en',
    questions: survey?.pages.flatMap(p => p.questions) ?? [],
    //to handle mutliple page strcuture
  };
}
